import { cloneElement } from "react";
import { LineChart, PieChart, CalendarCheck, TrendingUp, Lock, Smartphone } from "lucide-react";

/**
 * Render a section that showcases various financial management features.
 * @example
 * FeaturesSection()
 * <section>...</section>
 * @param {void} No arguments - This function does not accept any parameters.
 * @returns {JSX.Element} A section element containing a grid of feature cards for financial management.
 * @description
 *   - Uses a pre-defined list of features each with an icon, title, and description to render individual feature cards.
 *   - Implements responsive design with classes for different screen sizes.
 *   - Applies transitions and hover effects for interactive user experience.
 *   - Utilizes styling and layout classes from a CSS framework.
 */
export function FeaturesSection() {
  const features = [
    {
      icon: <LineChart className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Multi-Asset Portfolio",
      description:
        "Track 12+ asset types including PF, PPF, VPF, Gold, Crypto, Real Estate, and more in one dashboard.",
      badge: "NEW",
    },
    {
      icon: <PieChart className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Family Wealth Management",
      description:
        "Consolidated family net worth, member profiles, shared goals, and role-based access for your household.",
      badge: "POPULAR",
    },
    {
      icon: <CalendarCheck className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Smart Categories",
      description:
        "Customize your portfolio with flexible category management and auto-categorization.",
    },
    {
      icon: <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Real-Time Insights",
      description:
        "Get instant portfolio analytics with interactive charts and performance metrics.",
    },
    {
      icon: <Lock className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Bank-Grade Security",
      description:
        "End-to-end encryption ensures your sensitive financial data stays private and secure.",
    },
    {
      icon: <Smartphone className="h-5 w-5 md:h-6 md:w-6 text-primary" />,
      title: "Mobile-First Design",
      description:
        "Seamlessly manage your wealth on any device with our responsive, touch-optimized interface.",
      badge: "NEW",
    },
  ];

  return (
    <section className="container mx-auto px-4 py-8 md:py-12">
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-3 md:mb-4">
          Family Wealth Management, Built In
        </h2>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Powerful tools for personal finance and household wealth — designed for Indian investors
          and families
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
        {features.map((feature) => (
          <div
            key={feature.title}
            className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-orange-100 hover:shadow-lg transition-all hover:border-orange-300 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between mb-3 md:mb-4">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center">
                {cloneElement(feature.icon, { className: "h-6 w-6 md:h-7 md:w-7 text-orange-600" })}
              </div>
              {feature.badge && (
                <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full">
                  {feature.badge}
                </span>
              )}
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2 text-gray-900">{feature.title}</h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
